import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { Toggle } from '@actual-app/components/toggle';
import { View } from '@actual-app/components/view';

import { BocadilloGavi } from '#components/mobile/importar/BocadilloGavi';
import { nombrePantalla } from '#components/mobile/puente/puente';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { color, radio, texto } from '#components/mobile/ui/tokens';
import { pedirPuente, versionUso } from '#uso';

import { aparatoDe, fotoJpeg } from './captura';
import type { FotoPantalla } from './captura';

const MAX_TEXTO = 1000;

type Estado = 'escribiendo' | 'enviando' | 'enviado' | 'error' | 'demasiados';

type HojaAvisarProblemaProps = {
  /** La foto ya hecha; null si no se pudo hacer (se envía sin foto). */
  foto: FotoPantalla | null;
  /** Ruta sin ids (`/accounts/:id`), como en los datos de uso. */
  pantalla: string;
  onCerrar: () => void;
};

/**
 * «¿Qué falla en esta pantalla?»: la foto (cifras tapadas de serie), qué
 * pasa, y dos botones del mismo tamaño, con y sin foto (RGPD: el permiso es
 * libre). Llega al Puente de mando como un «Algo no va» con su foto.
 */
export function HojaAvisarProblema({
  foto,
  pantalla,
  onCerrar,
}: HojaAvisarProblemaProps) {
  const { t } = useTranslation();
  const [tapar, setTapar] = useState(true);
  const [textoLibre, setTextoLibre] = useState('');
  const [estado, setEstado] = useState<Estado>('escribiendo');
  const [vista, setVista] = useState(() =>
    foto ? fotoJpeg(foto, true) : null,
  );
  const aparato = aparatoDe(navigator.userAgent);
  const version = versionUso();

  const cambiarTapar = (si: boolean) => {
    setTapar(si);
    if (foto) {
      setVista(fotoJpeg(foto, si));
    }
  };

  const enviar = async (conFoto: boolean) => {
    setEstado('enviando');
    try {
      await pedirPuente('/peticion', {
        metodo: 'POST',
        cuerpo: {
          tipo: 'fallo',
          texto: textoLibre.trim(),
          pantalla,
          version,
          aparato,
          ...(conFoto && vista ? { foto: vista } : {}),
        },
      });
      setEstado('enviado');
    } catch (e) {
      setEstado(
        e instanceof Error && e.message === 'too-many' ? 'demasiados' : 'error',
      );
    }
  };

  if (estado === 'enviado') {
    return (
      <Hoja
        aria-label={t('Report a problem here')}
        data-testid="hoja-avisar-problema"
        onCerrar={onCerrar}
      >
        <View style={{ gap: 16, paddingTop: 4 }}>
          <BocadilloGavi estado="aplaude" testId="avisar-problema-enviado">
            <Trans>
              Received. I read everything and, if I need more details, I will
              write to you by email. It is already flying, faster than me
              chasing a coin.
            </Trans>
          </BocadilloGavi>
          <Boton bloque onPress={onCerrar} data-testid="avisar-problema-listo">
            <Trans>Got it</Trans>
          </Boton>
        </View>
      </Hoja>
    );
  }

  const sinTexto = textoLibre.trim() === '';
  const enviando = estado === 'enviando';

  return (
    <Hoja
      aria-label={t('Report a problem here')}
      data-testid="hoja-avisar-problema"
      onCerrar={onCerrar}
    >
      <View style={{ gap: 12 }}>
        <Text style={{ ...texto.display, fontSize: 21, color: color.fg }}>
          <Trans>What is wrong on this screen?</Trans>
        </Text>
        <BocadilloGavi>
          {vista ? (
            <Trans>
              Tell me in your own words; the photo is already taken. The figures
              are covered: nosy, yes, but with manners.
            </Trans>
          ) : (
            <Trans>
              Tell me in your own words what happened. I could not take the
              photo this time, so the more detail the better.
            </Trans>
          )}
        </BocadilloGavi>

        {vista && (
          <View
            style={{
              flexDirection: 'row',
              gap: 12,
              alignItems: 'center',
              backgroundColor: color.surface2,
              borderRadius: radio.boton,
              padding: 10,
            }}
          >
            <img
              src={vista}
              alt={t('Photo that will be sent')}
              data-testid="avisar-problema-foto"
              style={{
                width: 74,
                height: 160,
                objectFit: 'cover',
                objectPosition: 'top',
                borderRadius: 10,
                boxShadow: `0 0 0 1px ${color.line}`,
                flexShrink: 0,
              }}
            />
            <View style={{ flex: 1, gap: 8 }}>
              <Text style={{ ...texto.fila, color: color.fg }}>
                <Trans>The photo of this screen</Trans>
              </Text>
              <Text style={{ ...texto.secundario, color: color.fg2 }}>
                <Trans>
                  What you see here is exactly what arrives. Only the person who
                  runs Rumbo sees it.
                </Trans>
              </Text>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  minHeight: 44,
                }}
              >
                <Text style={{ ...texto.fila, fontSize: 14, color: color.fg }}>
                  <Trans>Cover the figures</Trans>
                </Text>
                <Toggle
                  id="avisar-problema-tapar"
                  aria-label={t('Cover the figures')}
                  isOn={tapar}
                  onToggle={cambiarTapar}
                />
              </View>
            </View>
          </View>
        )}

        <textarea
          aria-label={t('What were you trying to do and what happened?')}
          data-testid="avisar-problema-texto"
          value={textoLibre}
          maxLength={MAX_TEXTO}
          rows={3}
          placeholder={t('What were you trying to do and what happened?')}
          onChange={e => {
            setTextoLibre(e.target.value);
            if (estado === 'error' || estado === 'demasiados') {
              setEstado('escribiendo');
            }
          }}
          style={{
            width: '100%',
            minHeight: 90,
            borderRadius: radio.boton,
            backgroundColor: color.surface2,
            border: `1px solid ${color.line}`,
            padding: '12px 14px',
            fontSize: 16,
            lineHeight: 1.4,
            color: color.fg,
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            resize: 'vertical',
          }}
        />

        <View style={{ gap: 6 }}>
          <Text style={{ ...texto.secundario, color: color.fg3 }}>
            <Trans>This also goes, to find the problem:</Trans>
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {[
              t('Screen: {{name}}', { name: nombrePantalla(t, pantalla) }),
              version ? t('Rumbo {{version}}', { version }) : null,
              aparato,
            ]
              .filter(Boolean)
              .map(dato => (
                <Text
                  key={dato}
                  style={{
                    ...texto.pequeno,
                    fontWeight: 600,
                    color: color.fg2,
                    backgroundColor: color.surface2,
                    borderRadius: 999,
                    padding: '4px 10px',
                  }}
                >
                  {dato}
                </Text>
              ))}
          </View>
        </View>

        {estado === 'error' && (
          <Text style={{ ...texto.secundario, color: color.bad }}>
            <Trans>It could not be sent. Try again in a while.</Trans>
          </Text>
        )}
        {estado === 'demasiados' && (
          <Text style={{ ...texto.secundario, color: color.bad }}>
            <Trans>That is a lot for today. Try again tomorrow.</Trans>
          </Text>
        )}

        {vista ? (
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <View style={{ flex: 1 }}>
              <Boton
                bloque
                variante="fantasma"
                data-testid="avisar-problema-sin-foto"
                isDisabled={sinTexto || enviando}
                onPress={() => void enviar(false)}
              >
                <Trans>Send without photo</Trans>
              </Boton>
            </View>
            <View style={{ flex: 1 }}>
              <Boton
                bloque
                data-testid="avisar-problema-con-foto"
                isDisabled={sinTexto || enviando}
                onPress={() => void enviar(true)}
              >
                <Trans>Send with the photo</Trans>
              </Boton>
            </View>
          </View>
        ) : (
          <Boton
            bloque
            data-testid="avisar-problema-sin-foto"
            isDisabled={sinTexto || enviando}
            onPress={() => void enviar(false)}
          >
            <Trans>Send</Trans>
          </Boton>
        )}
      </View>
    </Hoja>
  );
}
